// utils/ringSound.js
// Synthesized periodic bell ring sound for the "Get it for free" CTA button (2 seconds duration)
// Uses Web Audio API without requiring any external audio files.

let ringCtx = null;
let activeRingSession = null;

function getContext() {
  const AudioContext = typeof window !== "undefined" && (window.AudioContext || window.webkitAudioContext);
  if (!AudioContext) return null;
  if (!ringCtx) {
    try {
      ringCtx = new AudioContext();
    } catch {
      return null;
    }
  }
  if (ringCtx && ringCtx.state === "suspended") {
    ringCtx.resume().catch(() => {});
  }
  return ringCtx;
}

// Automatically prepare/unlock AudioContext on first user interaction
if (typeof window !== "undefined") {
  const unlock = () => {
    try {
      getContext();
    } catch {}
    ["pointerdown", "touchstart", "click", "keydown", "scroll"].forEach((evt) => {
      window.removeEventListener(evt, unlock, { capture: true });
    });
  };
  ["pointerdown", "touchstart", "click", "keydown", "scroll"].forEach((evt) => {
    window.addEventListener(evt, unlock, { capture: true, passive: true });
  });
}

/**
 * Plays a realistic telephone / desk bell ringing sound for ~2 seconds (or until stopped).
 * Ring pattern: 2 burst cycles within 2 seconds
 */
export function startRingSound(durationMs = 2000) {
  stopRingSound();

  const ctx = getContext();
  if (!ctx) return;

  // If state is suspended, resume it
  if (ctx.state === "suspended") {
    ctx.resume().catch(() => {});
  }

  try {
    const masterGain = ctx.createGain();
    masterGain.gain.setValueAtTime(0.18, ctx.currentTime);
    masterGain.connect(ctx.destination);

    const startTime = ctx.currentTime + 0.04;
    const nodes = [];

    // Ring burst timing within the duration:
    // For 2 seconds: Burst 1 (0.0s to 0.85s), pause (0.85s to 1.05s), Burst 2 (1.05s to 1.9s)
    const bursts = durationMs <= 2500
      ? [
          { start: 0.0, end: 0.85 },
          { start: 1.05, end: 1.9 },
        ]
      : [
          { start: 0.0, end: 1.1 },
          { start: 1.9, end: 3.0 },
          { start: 3.8, end: 4.9 },
        ];

    bursts.forEach(({ start, end }) => {
      let strikeIndex = 0;
      for (let t = start; t < end; t += 0.075) {
        const strikeTime = startTime + t;
        const strikeGain = ctx.createGain();
        strikeGain.connect(masterGain);

        // Fast bell strike attack & natural decay
        strikeGain.gain.setValueAtTime(0.0001, strikeTime);
        strikeGain.gain.exponentialRampToValueAtTime(0.16, strikeTime + 0.008);
        strikeGain.gain.exponentialRampToValueAtTime(0.0001, strikeTime + 0.068);

        // Alternating bell dual tones (giving the classic mechanical bell trill ring)
        const freqA = strikeIndex % 2 === 0 ? 853 : 960;
        const freqB = strikeIndex % 2 === 0 ? 1280 : 1440;

        const oscA = ctx.createOscillator();
        oscA.type = "sine";
        oscA.frequency.setValueAtTime(freqA, strikeTime);
        oscA.connect(strikeGain);
        oscA.start(strikeTime);
        oscA.stop(strikeTime + 0.07);

        const oscB = ctx.createOscillator();
        oscB.type = "triangle";
        oscB.frequency.setValueAtTime(freqB, strikeTime);
        oscB.connect(strikeGain);
        oscB.start(strikeTime);
        oscB.stop(strikeTime + 0.07);

        nodes.push(oscA, oscB, strikeGain);
        strikeIndex++;
      }
    });

    // Master fade out at end of duration
    const stopTime = startTime + (durationMs / 1000);
    masterGain.gain.setValueAtTime(0.18, Math.max(startTime, stopTime - 0.1));
    masterGain.gain.linearRampToValueAtTime(0.0001, stopTime);

    const timer = setTimeout(() => {
      stopRingSound();
    }, durationMs + 80);

    activeRingSession = {
      stop: () => {
        clearTimeout(timer);
        try {
          masterGain.gain.cancelScheduledValues(ctx.currentTime);
          masterGain.gain.linearRampToValueAtTime(0.0001, ctx.currentTime + 0.03);
          setTimeout(() => {
            nodes.forEach((n) => {
              try { if (n.stop) n.stop(); n.disconnect(); } catch {}
            });
            masterGain.disconnect();
          }, 40);
        } catch {}
      },
    };
  } catch (err) {
    console.warn("Could not play ring sound:", err.message);
  }
}

export function stopRingSound() {
  if (activeRingSession) {
    try {
      activeRingSession.stop();
    } catch {}
    activeRingSession = null;
  }
}
